import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, saveAuth, clearAuth, getUser } from "./api";
import { sendEmail } from "./emailjs";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getUser());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      saveAuth(null, data);
      return data;
    } catch (e) {
      // 401 → cookie absent/expired; anything else → log and clear local cache.
      const status = e?.response?.status;
      if (status && status !== 401) {
        console.warn("[auth] refresh failed:", status, e?.message);
      }
      clearAuth();
      setUser(null);
      return null;
    }
  }, []);

  // Always re-hydrate from the server on mount so cached user data stays in
  // sync with the actual session cookie state.
  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      saveAuth(null, data.user);
      setUser(data.user);
      return data.user;
    } finally { setLoading(false); }
  }, []);

  const register = useCallback(async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", payload);
      saveAuth(null, data.user);
      setUser(data.user);
      sendEmail("welcome_new_user", {
        to_email: data.user.email,
        to_name: data.user.full_name,
        role: data.user.role,
        message: "Welcome to Safe Harbor Veterans — your account has been created.",
      });
      return data.user;
    } finally { setLoading(false); }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) {
      // Logout is best-effort — even if the network call fails the local state
      // is wiped so the UI immediately reflects signed-out.
      console.warn("[auth] logout request failed:", e?.response?.status || e?.message);
    }
    clearAuth();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
